import type { ShotView, TimelineClip, TimelineView as TimelineViewData } from "@dramapie/shared";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

import { formatClock, formatDuration } from "../../lib/format";
import {
  secondsAtX,
  timelinePercent,
  timelineTicks,
} from "../../lib/m6";
import { SHOT_STATUS_META, shortId } from "../../lib/status";
import { EmptyState } from "../states";

export interface TimelineViewProps {
  data: TimelineViewData;
  /** 故事板里的镜头，用来补动作 / 景别（时间线只管时间，不重复领域数据）。 */
  shots?: readonly ShotView[];
  activeShotId: string | null;
  onSelectShot: (shotId: string) => void;
  /** 「在故事板打开」：切回故事板并定位当前镜。 */
  onOpenStoryboard?: (shotId: string) => void;
}

function clipAt(clips: readonly TimelineClip[], time: number): TimelineClip | null {
  if (clips.length === 0) return null;
  return (
    clips.find((clip) => time >= clip.start && time < clip.start + clip.duration) ??
    clips[clips.length - 1] ??
    null
  );
}

/**
 * 时间线（契约 M6 §2，只读）。
 *
 * 两条轨道：`画面`（有视频用 `<video poster>`，没有就用首帧）与 `台词`（文本块）。
 * 播放头 = 一个全局时间游标：点击 / 拖拽轨道定位，播放时按顺序走片段；
 * 没有视频的片段显示首帧并停留 `duration` 秒（时间游标不依赖媒体加载）。
 */
export function TimelineView({
  data,
  shots,
  activeShotId,
  onSelectShot,
  onOpenStoryboard,
}: TimelineViewProps) {
  const total = Number.isFinite(data.totalSeconds) ? Math.max(0, data.totalSeconds) : 0;
  const clips = useMemo(
    () => [...data.clips].sort((a, b) => a.start - b.start),
    [data.clips],
  );
  const dialogues = useMemo(
    () => [...data.dialogues].sort((a, b) => a.start - b.start),
    [data.dialogues],
  );
  const shotById = useMemo(
    () => new Map((shots ?? []).map((shot) => [shot.id, shot] as const)),
    [shots],
  );

  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const seekingRef = useRef(false);

  const current = useMemo(() => clipAt(clips, time), [clips, time]);
  const currentShot = current ? (shotById.get(current.shotId) ?? null) : null;
  const currentDialogue = current
    ? (dialogues.find((line) => line.shotId === current.shotId) ?? null)
    : null;
  const playhead = timelinePercent(time, total);

  const seek = useCallback(
    (seconds: number) => {
      const value = Math.min(total, Math.max(0, seconds));
      setTime(total > 0 ? value : 0);
    },
    [total],
  );

  // 播放：100ms 心跳推进全局时间；媒体只跟随这个游标，不用依赖 video 的 ended 事件。
  useEffect(() => {
    if (!playing || total <= 0) return;
    const id = window.setInterval(() => {
      setTime((prev) => Math.min(total, Math.round((prev + 0.1) * 10) / 10));
    }, 100);
    return () => window.clearInterval(id);
  }, [playing, total]);

  useEffect(() => {
    if (playing && total > 0 && time >= total) setPlaying(false);
  }, [playing, time, total]);

  // 播放 / 定位时把当前片段对齐到 video；没有视频的片段只显示 poster。
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !current) return;
    const offset = Math.min(current.duration, Math.max(0, time - current.start));
    if (video.readyState > 0 && Math.abs(video.currentTime - offset) > 0.35) {
      try {
        video.currentTime = offset;
      } catch {
        // 元数据还没到（或 jsdom 不支持 seek）：保持 poster 即可。
      }
    }
    if (playing && video.readyState > 0) {
      try {
        void video.play()?.catch?.(() => {});
      } catch {
        // 浏览器拒绝自动播放 / 测试环境没有媒体实现：时间线照常走时间。
      }
    } else if (!video.paused) {
      video.pause();
    }
  }, [playing, current, time]);

  const seekFromClientX = useCallback(
    (clientX: number, element: HTMLElement | null) => {
      const rect = element?.getBoundingClientRect();
      if (!rect || rect.width <= 0) return;
      seek(secondsAtX(clientX, rect, total));
    },
    [seek, total],
  );

  function handleTrackClick(event: ReactMouseEvent<HTMLElement>) {
    if ((event.target as HTMLElement).closest("button")) return;
    seekFromClientX(event.clientX, contentRef.current);
  }

  function handlePointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if ((event.target as HTMLElement).closest("button")) return;
    seekingRef.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    seekFromClientX(event.clientX, contentRef.current);
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!seekingRef.current) return;
    seekFromClientX(event.clientX, contentRef.current);
  }

  function endSeeking(event: ReactPointerEvent<HTMLDivElement>) {
    seekingRef.current = false;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      setPlaying((prev) => !prev);
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      seek(time - 1);
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      seek(time + 1);
    }
  }

  if (clips.length === 0) {
    return (
      <div className="dp-timeline" aria-label="时间线视图">
        <div className="dp-tl-empty">
          <EmptyState
            title="这一集还没有镜头"
            description="时间线按 shot.order 和 duration 排布；先在故事板里添加镜头。"
          />
        </div>
      </div>
    );
  }

  const ticks = timelineTicks(total);
  const clipLeft = (clip: TimelineClip) => timelinePercent(clip.start, total);
  const clipWidth = (clip: TimelineClip) => timelinePercent(clip.duration, total);
  const currentCode = current ? shortId(current.shotId) : "—";

  return (
    <div
      className="dp-timeline"
      aria-label="时间线视图"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <header className="dp-tl-head">
        <div className="dp-tl-heading">
          <p className="dp-tl-eyebrow">
            时间线 · 第 {data.episode.no} 话 · {data.episode.title}
          </p>
          <h2 className="dp-tl-title">
            {data.episode.title}
            <small>
              {clips.length} 镜 · {data.episode.aspect}
            </small>
          </h2>
        </div>
        <div className="dp-tl-controls">
          <button
            type="button"
            className="dp-btn primary sm"
            aria-label={playing ? "暂停" : "播放"}
            aria-pressed={playing}
            onClick={() => setPlaying((prev) => !prev)}
          >
            {playing ? "暂停" : "播放"}
          </button>
          <span className="dp-tl-time" aria-live="off" aria-label="当前时间">
            {formatClock(time)} / {formatClock(total)}
          </span>
          <span className="dp-tl-total" aria-label="总时长">
            总时长 <b>{formatClock(total)}</b>
          </span>
          <button
            type="button"
            className="dp-btn sm"
            disabled={!current || !onOpenStoryboard}
            title="切回故事板并定位当前镜"
            onClick={() => {
              if (current) onOpenStoryboard?.(current.shotId);
            }}
          >
            在故事板打开
          </button>
        </div>
      </header>

      <div className="dp-tl-preview">
        <div className="dp-tl-poster">
          {current?.media?.url ? (
            <video
              key={current.shotId}
              ref={videoRef}
              src={current.media.url}
              poster={current.poster?.url ?? undefined}
              muted
              playsInline
              preload="metadata"
              aria-label={`第 ${currentShot?.order ?? currentCode} 镜视频`}
            />
          ) : current?.poster?.url ? (
            <img src={current.poster.url} alt={`${currentCode} 首帧`} loading="lazy" />
          ) : (
            <span className="dp-tl-noimg" aria-hidden="true">
              无画面
            </span>
          )}
        </div>
        <div className="dp-tl-now">
          <div className="dp-tl-now-id">
            {currentCode}
            <span>
              {currentShot?.framing ? `${currentShot.framing} · ` : ""}
              {formatDuration(current?.duration ?? 0)}
            </span>
          </div>
          {currentShot?.action ? <p className="dp-tl-action">{currentShot.action}</p> : null}
          <p className="dp-tl-line">
            {currentDialogue ? (
              <>
                <b>{currentDialogue.who ?? "旁白"}</b>
                {currentDialogue.text}
              </>
            ) : (
              "—"
            )}
          </p>
        </div>
      </div>

      <div className="dp-tl-scroll">
        <div className="dp-tl-sheet">
          <div className="dp-tl-labels" aria-hidden="true">
            <div className="dp-tl-label-spacer" />
            <div className="dp-tl-label">V1 画面</div>
            <div className="dp-tl-label">A1 台词</div>
          </div>

          <div
            ref={contentRef}
            className="dp-tl-content"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endSeeking}
            onPointerCancel={endSeeking}
          >
            <div className="dp-tl-ruler" onClick={handleTrackClick} aria-hidden="true">
              {ticks.map((tick) => (
                <span
                  key={`tick-${tick}`}
                  className="dp-tl-tick"
                  style={{ left: `${timelinePercent(tick, total)}%` }}
                >
                  <span>{formatClock(tick)}</span>
                </span>
              ))}
            </div>

            <div
              className="dp-tl-lane is-video"
              onClick={handleTrackClick}
              role="presentation"
            >
              {clips.map((clip) => {
                const meta = SHOT_STATUS_META[clip.status];
                const code = shortId(clip.shotId);
                const videoUrl = clip.media?.kind === "video" ? clip.media.url : null;
                const posterUrl = clip.poster?.url ?? null;
                return (
                  <button
                    key={`clip-${clip.shotId}`}
                    type="button"
                    className={`dp-tl-block is-clip${clip.shotId === activeShotId ? " is-active" : ""}`}
                    data-shot-id={clip.shotId}
                    data-start={clip.start}
                    data-duration={clip.duration}
                    aria-label={`镜头 ${code} · ${meta.label} · ${formatDuration(clip.duration)}`}
                    aria-pressed={clip.shotId === activeShotId}
                    title={`${code} · ${formatDuration(clip.duration)} · ${meta.label}`}
                    style={
                      {
                        left: `calc(${clipLeft(clip)}% + 1px)`,
                        width: `calc(${clipWidth(clip)}% - 2px)`,
                      } as CSSProperties
                    }
                    onClick={() => {
                      seek(clip.start);
                      onSelectShot(clip.shotId);
                    }}
                  >
                    {videoUrl ? (
                      <video
                        src={videoUrl}
                        poster={posterUrl ?? undefined}
                        muted
                        playsInline
                        preload="none"
                        aria-hidden="true"
                        tabIndex={-1}
                      />
                    ) : posterUrl ? (
                      <img src={posterUrl} alt="" loading="lazy" draggable={false} />
                    ) : (
                      <span className={`dp-tl-block-ph is-${meta.tone}`} aria-hidden="true" />
                    )}
                    <span className="dp-tl-block-label">{code}</span>
                  </button>
                );
              })}
            </div>

            <div
              className="dp-tl-lane is-dialogue"
              onClick={handleTrackClick}
              role="presentation"
            >
              {dialogues.map((line, index) => (
                <button
                  key={`dialogue-${line.shotId}-${index}`}
                  type="button"
                  className={`dp-tl-block is-dialogue${
                    line.shotId === activeShotId ? " is-active" : ""
                  }`}
                  data-shot-id={line.shotId}
                  data-start={line.start}
                  data-duration={line.duration}
                  aria-label={`台词 ${line.who ?? "旁白"}：${line.text}`}
                  title={`${line.who ?? "旁白"}：${line.text}`}
                  style={{
                    left: `${timelinePercent(line.start, total)}%`,
                    width: `${timelinePercent(line.duration, total)}%`,
                  }}
                  onClick={() => {
                    seek(line.start);
                    onSelectShot(line.shotId);
                  }}
                >
                  {line.who ? <b>{line.who}</b> : null}
                  <span>{line.text}</span>
                </button>
              ))}
            </div>

            <div className="dp-tl-playhead" style={{ left: `${playhead}%` }} aria-hidden="true" />
          </div>
        </div>

        <div className="dp-tl-foot" aria-hidden="true">
          <span>
            <i className="dp-tl-swatch is-video" />
            画面
          </span>
          <span>
            <i className="dp-tl-swatch is-dialogue" />
            台词
          </span>
          <span className="dp-tl-foot-note">点轨道定位 · 拖播放头 · 空格播放</span>
        </div>
      </div>
    </div>
  );
}
